import { useCallback, useEffect, useMemo, useState } from 'react';
import { maintenanceService, Maintenance } from '../../api/maintenance';
import { vehicleService, Vehicle } from '../../api/vehicles';
import { providerService, Provider } from '../../api/provedor';
import FilterOptions from './components/FilterOptions';
import {
  Search,
  Car,
  Calendar,
  FileText,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Filter,
  Wrench,
  Banknote,
  Building2,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Button,
  Modal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const statusLabelMap = {
  pending: 'Pendente',
  active: 'Em execução',
  rejected: 'Rejeitada',
  concluded: 'Concluída',
} as const;

const statusClassMap = {
  pending: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  active: 'bg-blue-50 text-blue-800 ring-blue-600/20',
  rejected: 'bg-rose-50 text-rose-800 ring-rose-600/20',
  concluded: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
} as const;

const HistoricoDeManutencao = () => {
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [selectedMaintenance, setSelectedMaintenance] = useState<Maintenance | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2800); return () => clearTimeout(t); } }, [toast]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [allMaintenances, allVehicles, allProviders] = await Promise.all([
        maintenanceService.getHistorical(),
        vehicleService.getAllVehicles().catch(() => []),
        providerService.getAll().catch(() => []),
      ]);
      setMaintenances(allMaintenances);
      setVehicles(allVehicles);
      setProviders(allProviders);
    } catch (err: unknown) {
      console.error('Erro ao carregar histórico de manutenções:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filterCategories = useMemo(() => ({
    Veículo: Array.from(new Set(maintenances.map((item) => item.vehicle_id)))
      .map((vehicleId) => {
        const v = vehicles.find((item) => item.id === vehicleId);
        return {
          id: String(vehicleId),
          label: v ? `${v.brand} - ${v.license_plate}` : `Veículo ${vehicleId}`,
        };
      }),
    Fornecedor: Array.from(new Set(maintenances.map((item) => item.provider_id)))
      .map((providerId) => {
        const p = providers.find((item) => item.id === providerId);
        return {
          id: String(providerId),
          label: p ? p.name : `Fornecedor ${providerId}`,
        };
      }),
  }), [maintenances, vehicles, providers]);

  const getVehicleLabel = useCallback((vehicleId: number) => {
    const v = vehicles.find((item) => item.id === vehicleId);
    return v ? { primary: v.brand, secondary: `Placa: ${v.license_plate}`, display: `${v.brand} - ${v.license_plate}` } : { primary: `Veículo ${vehicleId}`, secondary: '', display: `Veículo ${vehicleId}` };
  }, [vehicles]);

  const getProviderLabel = useCallback((providerId: number) => {
    const p = providers.find((item) => item.id === providerId);
    return p ? { primary: p.name, secondary: p.cnpj ? `CNPJ: ${p.cnpj}` : '', display: p.name } : { primary: `Fornecedor ${providerId}`, secondary: '', display: `Fornecedor ${providerId}` };
  }, [providers]);

  const filteredMaintenances = useMemo(() => maintenances.filter(m => {
    const term = searchTerm.trim().toLowerCase();
    const vehicleText = getVehicleLabel(m.vehicle_id).display.toLowerCase();
    const providerText = getProviderLabel(m.provider_id).display.toLowerCase();

    const matchesSearch =
      !term ||
      String(m.id).includes(term) ||
      String(m.vehicle_id).includes(term) ||
      String(m.provider_id).includes(term) ||
      vehicleText.includes(term) ||
      providerText.includes(term) ||
      (m.reason || '').toLowerCase().includes(term) ||
      (m.entry_date || '').toLowerCase().includes(term) ||
      (m.estimated_completion || '').toLowerCase().includes(term);

    const selectedVehicles = appliedFilters.Veículo || [];
    const matchesVehicle =
      selectedVehicles.length === 0 || selectedVehicles.includes(String(m.vehicle_id));

    const selectedProviders = appliedFilters.Fornecedor || [];
    const matchesProvider =
      selectedProviders.length === 0 || selectedProviders.includes(String(m.provider_id));

    return matchesSearch && matchesVehicle && matchesProvider;
  }), [maintenances, searchTerm, appliedFilters, getVehicleLabel, getProviderLabel]);

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(filteredMaintenances.length / ITEMS_PER_PAGE)),
    [filteredMaintenances.length],
  );

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [currentPage, totalPaginas]);

  const { maintenancesPaginados, pageRange } = useMemo(() => {
    const total = filteredMaintenances.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      maintenancesPaginados: filteredMaintenances.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredMaintenances, currentPage, totalPaginas]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('pt-BR'); }
    catch { return '-'; }
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const handleViewMaintenance = (m: Maintenance) => {
    setSelectedMaintenance(m);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setSelectedMaintenance(null);
    setIsViewModalOpen(false);
  };

  return (
    <>
      <PageHeader
        title="Histórico de Manutenção"
        description="Serviços registrados · Histórico completo de manutenções — acompanhe todas as manutenções com veículos, fornecedores e valores."
        hideTitleInBody
      />

      {toast && (
        <div className={`fixed top-5 right-5 z-[100] inline-flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] border animate-[fadeIn_0.2s_ease-out] ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
          <span className="text-[13px] font-semibold leading-tight">{toast.message}</span>
        </div>
      )}

      <div className="flex flex-col gap-5">
        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Todas as manutenções</h2>
                <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                  Concluídas e rejeitadas, com detalhes completos
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1 sm:max-w-xl">
                <div className="relative flex-1 min-w-0">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    placeholder="Buscar ID, veículo, fornecedor, motivo..."
                    className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pl-10 text-sm text-slate-900 placeholder:text-slate-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 h-10"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => setIsFilterOpen(true)}
                  className="h-10 shrink-0"
                >
                  <Filter className="h-4 w-4 stroke-[1.9]" />
                  Filtros
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => { setSubmitting(true); void fetchData().finally(() => setSubmitting(false)); }}
                  title="Atualizar informações"
                  className="h-10 shrink-0"
                  disabled={submitting}
                >
                  <RefreshCw className={`h-4 w-4 stroke-[1.9] ${submitting ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && maintenances.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">Carregando histórico...</span>
              </div>
            ) : (
              <ModernTable className="table-fixed layout-fixed">
                <Thead>
                  <Tr>
                    <Th className="w-[8%] min-w-[90px] max-w-[120px]">ID</Th>
                    <Th className="w-[16%] min-w-[200px] max-w-[280px]">Veículo</Th>
                    <Th className="w-[18%] min-w-[220px] max-w-[300px]">Fornecedor</Th>
                    <Th className="w-[20%] min-w-[220px] max-w-[320px]">Motivo</Th>
                    <Th className="w-[10%] min-w-[130px] max-w-[170px]">Entrada</Th>
                    <Th className="w-[10%] min-w-[130px] max-w-[170px]">Previsão</Th>
                    <Th className="w-[10%] min-w-[140px] max-w-[180px]">Valor Final</Th>
                    <Th className="w-[6%] min-w-[80px] max-w-[100px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {maintenancesPaginados.length === 0 ? (
                    <Tr>
                      <Td colSpan={8}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <Wrench className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">Nenhuma manutenção encontrada</div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm ? 'Nenhum resultado para a busca atual. Tente outro termo.' : 'Não há manutenções históricas registradas no sistema.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    maintenancesPaginados.map((m) => {
                      const statusLabel = statusLabelMap[m.status] || m.status;
                      const statusClasses = statusClassMap[m.status] || '';
                      const vehicleLabel = getVehicleLabel(m.vehicle_id);
                      const providerLabel = getProviderLabel(m.provider_id);
                      return (
                        <Tr key={m.id}>
                          <Td>
                            <div className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 ring-1 ring-inset ring-slate-200">
                              <span className="text-[12.5px] font-bold leading-none tabular-nums text-slate-700">#{m.id}</span>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[280px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                  <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={vehicleLabel.display}>
                                    {vehicleLabel.primary}
                                  </span>
                                  {vehicleLabel.secondary && (
                                    <span className="block truncate text-[11.5px] font-medium leading-tight text-slate-500 mt-0.5" title={vehicleLabel.secondary}>
                                      {vehicleLabel.secondary}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[300px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200">
                                  <Building2 className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={providerLabel.display}>
                                    {providerLabel.primary}
                                  </span>
                                  {providerLabel.secondary && (
                                    <span className="block truncate text-[11.5px] font-medium leading-tight text-slate-500 mt-0.5" title={providerLabel.secondary}>
                                      {providerLabel.secondary}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[320px] overflow-hidden py-1">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
                                  <FileText className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1 pt-0.5">
                                  <span className="block line-clamp-2 text-[13px] font-medium leading-snug text-slate-800" title={m.reason}>
                                    {m.reason}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(m.entry_date)}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(m.estimated_completion)}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-bold text-emerald-700 tabular-nums">
                                <Banknote className="h-[15px] w-[15px] text-emerald-600" />
                                {formatCurrency(m.final_price)}
                              </div>
                              <span className={`mt-2 inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${statusClasses}`}>
                                {statusLabel}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewMaintenance(m)}
                                title="Visualizar"
                                className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 hover:ring-blue-200"
                              >
                                <Eye className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                  {' manutenção(ões)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          disabled={submitting}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${n === currentPage ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]' : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'}`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isViewModalOpen && selectedMaintenance && (
        <Modal
          onClose={closeViewModal}
          maxWidthClass="max-w-3xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-orange-700 text-white shadow-[0_6px_16px_-8px_rgba(249,115,22,0.6)]">
                <Wrench className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes da Manutenção
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Informações completas da manutenção #{selectedMaintenance.id}
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
            </div>
          }
        >
          <div className="space-y-6">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">Dados da manutenção</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">ID</span>
                    <p className="text-sm text-slate-900 font-medium">#{selectedMaintenance.id}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Status</span>
                    <div className="mt-0.5">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${statusClassMap[selectedMaintenance.status] || ''}`}>
                        {statusLabelMap[selectedMaintenance.status] || selectedMaintenance.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Valor Final</span>
                    <p className="text-sm font-bold text-emerald-700 tabular-nums">{formatCurrency(selectedMaintenance.final_price)}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-slate-500">Motivo</span>
                  <p className="text-sm text-slate-900 font-medium whitespace-pre-wrap">{selectedMaintenance.reason}</p>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">Veículo e Fornecedor</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Veículo</span>
                    <div>
                      {(() => {
                        const vLabel = getVehicleLabel(selectedMaintenance.vehicle_id);
                        return (
                          <>
                            <p className="text-sm text-slate-900 font-medium">{vLabel.primary}</p>
                            {vLabel.secondary && <p className="text-[11.5px] font-semibold text-slate-500 mt-0.5">{vLabel.secondary}</p>}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Fornecedor</span>
                    <div>
                      {(() => {
                        const pLabel = getProviderLabel(selectedMaintenance.provider_id);
                        return (
                          <>
                            <p className="text-sm text-slate-900 font-medium">{pLabel.primary}</p>
                            {pLabel.secondary && <p className="text-[11.5px] font-semibold text-slate-500 mt-0.5">{pLabel.secondary}</p>}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-purple-500 to-purple-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-purple-700">Datas e Auditoria</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Data de Entrada</span>
                    <p className="text-sm text-slate-900 font-medium">{formatDate(selectedMaintenance.entry_date)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Previsão de Conclusão</span>
                    <p className="text-sm text-slate-900 font-medium">{formatDate(selectedMaintenance.estimated_completion)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Criado em</span>
                    <p className="text-sm text-slate-900 font-medium">{formatDate(selectedMaintenance.created_at)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Atualizado em</span>
                    <p className="text-sm text-slate-900 font-medium">{formatDate(selectedMaintenance.updated_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <FilterOptions
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilter={(f) => { setAppliedFilters(f); setCurrentPage(1); }}
        filterCategories={filterCategories}
        initialFilters={appliedFilters}
      />
    </>
  );
};

export default HistoricoDeManutencao;
